import Controller from "@ember/controller";
import { task, dropTask, timeout } from "ember-concurrency";
import { service } from "@ember/service";
import { cached, tracked } from "@glimmer/tracking";
import ENV from "glesys-controlpanel/config/environment";

const DEFAULT_SORT_BY = "created";
const DEFAULT_SORT_DIR = "desc";

const byId = (a, b) => a.id.localeCompare(b.id);
const byName = (a, b) => (a.description || a.id).localeCompare(b.description || b.id);
const byCreated = (a, b) => new Date(a.created) - new Date(b.created);
const bySize = (a, b) => (a.usage?.size ?? 0) - (b.usage?.size ?? 0);

export default class ObjectStorageOverviewController extends Controller {
  @service intl;
  @service http;
  @service notification;
  @service abilities;

  queryParams = ["sortBy", "sortDir"];

  @tracked sortBy = null;
  @tracked sortDir = null;

  get project() {
    return this.model.project;
  }

  get dataCenters() {
    return this.model.dataCenters;
  }

  @tracked instances = null;
  @tracked hasInstances = true;

  get availableDataCenters() {
    return this.dataCenters.filter((dataCenter) => dataCenter.hasObjectStorageInstanceSupport);
  }

  get totalInstances() {
    return this.fetchInstances.lastSuccessful?.value ?? [];
  }

  isFiltered = (instances) => instances.length < this.totalInstances.length;

  totalUsage = (instances) => instances.reduce((sum, instance) => sum + (instance.usage?.size ?? 0), 0);

  totalCost = (instances) => instances.reduce((sum, instance) => sum + (instance.billing?.total ?? 0), 0);

  get hasBilling() {
    return this.totalInstances.some((instance) => instance.billing);
  }

  @cached
  get sortOptions() {
    return [
      {
        label: this.intl.t("object-storage.overview.sort.group.name"),
        options: [
          {
            label: this.intl.t("object-storage.overview.sort.name-asc"),
            query: { sortBy: "name", sortDir: "asc" },
            compare: (a, b) => byName(a, b) || byId(a, b),
          },
          {
            label: this.intl.t("object-storage.overview.sort.name-desc"),
            query: { sortBy: "name", sortDir: "desc" },
            compare: (a, b) => byName(b, a) || byId(b, a),
          },
        ],
      },
      {
        label: this.intl.t("object-storage.overview.sort.group.date"),
        options: [
          {
            label: this.intl.t("object-storage.overview.sort.created-desc"),
            query: { sortBy: "created", sortDir: "desc" },
            compare: (a, b) => byCreated(b, a) || byName(b, a) || byId(b, a),
          },
          {
            label: this.intl.t("object-storage.overview.sort.created-asc"),
            query: { sortBy: "created", sortDir: "asc" },
            compare: (a, b) => byCreated(a, b) || byName(a, b) || byId(a, b),
          },
        ],
      },
      {
        label: this.intl.t("object-storage.overview.sort.group.size"),
        options: [
          {
            label: this.intl.t("object-storage.overview.sort.size-desc"),
            query: { sortBy: "size", sortDir: "desc" },
            compare: (a, b) => bySize(b, a) || byName(b, a) || byId(b, a),
          },
          {
            label: this.intl.t("object-storage.overview.sort.size-asc"),
            query: { sortBy: "size", sortDir: "asc" },
            compare: (a, b) => bySize(a, b) || byName(a, b) || byId(a, b),
          },
        ],
      },
    ];
  }

  get currentSort() {
    const sortBy = this.sortBy ?? DEFAULT_SORT_BY;
    const sortDir = this.sortDir ?? DEFAULT_SORT_DIR;
    const known = this.sortOptions
      .flatMap((item) => ("options" in item ? item.options : [item]))
      .some((option) => option.query.sortBy === sortBy && option.query.sortDir === sortDir);
    return known ? { sortBy, sortDir } : { sortBy: DEFAULT_SORT_BY, sortDir: DEFAULT_SORT_DIR };
  }

  setupController() {
    this.hasInstances = true;
    this.fetchInstances.perform();
  }

  resetController() {
    this.fetchInstances.cancelAll({ resetState: true });
    this.scheduleRefetchOfInstances.cancelAll();
  }

  deleteInstance = task(async (instance) => {
    await this.http
      .request({ url: `/objectstorage/deleteinstance?instanceid=${instance.id}`, method: "POST" })
      .then(() => {
        this.fetchInstances.perform();
        this.notification.add(
          "success",
          this.intl.t("object-storage.notifications.instance-deleted.title"),
          this.intl.t("object-storage.notifications.instance-deleted.text"),
        );
      });
  });

  scheduleRefetchOfInstances = dropTask(async () => {
    await timeout(60000);
    this.fetchInstances.perform();
  });

  fetchInstances = task(async () => {
    let projectkey = this.project.id;
    let [{ content }, costs] = await Promise.all([
      this.http.request({ url: `/objectstorage/listinstances?projectkey=${projectkey}` }),
      this.fetchCosts(projectkey),
    ]);

    this.hasInstances = !!content.instances.length;

    if (ENV.environment !== "test") {
      this.scheduleRefetchOfInstances.perform();
    }

    let costByKey = new Map(costs.map((cost) => [cost.key, cost]));
    return content.instances.map((instance) => ({ ...instance, billing: costByKey.get(instance.id) }));
  });

  async fetchCosts(projectkey) {
    if (this.abilities.cannot("access pricing for organization", this.project.organization)) {
      return [];
    }

    try {
      let { content } = await this.http.request({ url: `/objectstorage/costs?projectkey=${projectkey}` });
      return content.costs ?? [];
    } catch {
      return [];
    }
  }
}
